import { CalculatorFormValues } from '../schema';
import { CalculationResult } from './types';
import { calculateIncomeContribution } from './income-calculator';
import { calculateAssetContribution } from './asset-calculator';
import { calculateProjection } from './projection-calculator';

export function calculateContribution(data: CalculatorFormValues): CalculationResult {
  const WEEKS_IN_YEAR = 52;
  
  // Calculate income contribution
  const annualIncomeContribution = calculateIncomeContribution(data);
  const weeklyIncomeContribution = annualIncomeContribution / WEEKS_IN_YEAR;
  
  // Calculate asset contributions
  const assetContributions = calculateAssetContribution(data);
  const weeklyAssetContribution = assetContributions.totalAssetContribution / WEEKS_IN_YEAR;
  
  // Calculate weekly property contribution for loan purposes
  const propertyAssets = assetContributions.primaryResidenceContribution + 
                        assetContributions.otherPropertiesContribution + 
                        assetContributions.farmBusinessContribution;
  const weeklyPropertyContribution = propertyAssets / WEEKS_IN_YEAR;
  
  // Calculate deferred amount if using the nursing home loan
  let deferredAmount = 0;
  if (data.useLoan && propertyAssets > 0) {
    deferredAmount = weeklyPropertyContribution;
  }
  
  // Calculate total contribution
  const annualTotalContribution = annualIncomeContribution + assetContributions.totalAssetContribution;
  const weeklyTotalContribution = annualTotalContribution / WEEKS_IN_YEAR;
  
  // Calculate State support
  let stateSupport = null;
  if (data.nursingHomeCost > 0) {
    const weeklyNursingHomeCost = data.nursingHomeCost;
    stateSupport = Math.max(0, weeklyNursingHomeCost - weeklyTotalContribution);
  }
  
  // Generate 5-year projection
  const yearlyProjection = calculateProjection(
    data,
    annualIncomeContribution,
    {
      primaryResidence: assetContributions.primaryResidenceContribution,
      otherProperties: assetContributions.otherPropertiesContribution,
      farmBusiness: assetContributions.farmBusinessContribution,
      cash: assetContributions.cashAssetsContribution
    },
    WEEKS_IN_YEAR
  );
  
  // Calculate total deferred amount over 5 years
  const totalDeferredOver5Years = yearlyProjection.reduce((sum, year) => 
    sum + (year.deferredAmount * WEEKS_IN_YEAR), 0);
  
  return {
    weeklyContribution: weeklyTotalContribution,
    annualContribution: annualTotalContribution,
    incomeContribution: {
      weekly: weeklyIncomeContribution,
      annual: annualIncomeContribution
    },
    assetContribution: {
      weekly: weeklyAssetContribution,
      annual: assetContributions.totalAssetContribution,
      primaryResidence: assetContributions.primaryResidenceContribution / WEEKS_IN_YEAR,
      otherProperties: assetContributions.otherPropertiesContribution / WEEKS_IN_YEAR,
      farmBusiness: assetContributions.farmBusinessContribution / WEEKS_IN_YEAR,
      cash: assetContributions.cashAssetsContribution / WEEKS_IN_YEAR
    },
    stateSupport,
    yearlyProjection,
    deferredAmount,
    totalDeferredOver5Years
  };
}

// Keep the formatCurrency function
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}
